import { useStore } from '../store/StoreContext.jsx';
import { useLayout } from '../store/LayoutContext.jsx';
import { PQ_TABS, tabProgress, adjacentTabs } from '../schema/pqTabs.js';
import { getDashboardMetrics, formatDollars } from '../store/selectors.js';
import Icon from './Icon.jsx';
import ViewButton from './ViewButton.jsx';

function progressTone(pct) {
  if (pct === 0) return 'empty';
  if (pct >= 100) return 'complete';
  return 'partial';
}

// The PQ's navigation: four tabs pinned under the topbar, one per page of the
// questionnaire, with the notes toggle at the far end so the notes are one
// click away on every page. Replaces the side nav, whose ten-item list was
// the index of a page that no longer exists, and whose 56px slim rail was
// width the form wants back in a half-screen window.
//
// Every control here is a ViewButton: the bar sits inside the view-only
// fieldset, and a viewer still has to turn pages and open the notes.
export default function TabBar() {
  const { data } = useStore();
  const { activeTab, setActiveTab, notesOpen, setNotesOpen, railHidden, zoomMode } = useLayout();

  // Left and right arrows move between tabs, as a tablist is expected to.
  const onKeyDown = e => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    const { prev, next } = adjacentTabs(activeTab);
    const to = e.key === 'ArrowLeft' ? prev : next;
    if (!to) return;
    e.preventDefault();
    setActiveTab(to.id);
    document.getElementById(`tab-${to.id}`)?.focus();
  };

  // Not "pages": getByLabel('Age') in the browser suite is a substring match,
  // and "Questionnaire pages" would answer it.
  return (
    <nav className="tab-bar" aria-label="Questionnaire tabs">
      <div className="tab-bar-row">
        <div className="tab-list" role="tablist" onKeyDown={onKeyDown}>
          {PQ_TABS.map((t, i) => {
            const pct = tabProgress(t, data);
            const active = t.id === activeTab;
            return (
              <ViewButton
                key={t.id}
                id={`tab-${t.id}`}
                data-tab={t.id}
                role="tab"
                aria-selected={active}
                aria-controls={`tabpanel-${t.id}`}
                tabIndex={active ? 0 : -1}
                className={`tab is-${progressTone(pct)}` + (active ? ' is-active' : '')}
                title={`${t.label} (${pct}% complete)`}
                onClick={() => setActiveTab(t.id)}
              >
                <span className="tab-num">{i + 1}</span>
                <span className="tab-label">{t.label}</span>
                <span className="tab-label-short" aria-hidden="true">{t.short}</span>
                <span className="tab-pct">{pct}%</span>
                <span className="tab-progress" aria-hidden="true">
                  <span className="tab-progress-fill" style={{ width: `${pct}%` }} />
                </span>
              </ViewButton>
            );
          })}
        </div>
        <ViewButton
          className={'tab-notes-btn' + (notesOpen ? ' is-on' : '')}
          onClick={() => setNotesOpen(!notesOpen)}
          aria-pressed={notesOpen}
          aria-label={notesOpen ? 'Collapse notes (Ctrl+Q)' : 'Open notes (Ctrl+Q)'}
          title={notesOpen ? 'Collapse notes  (Ctrl+Q)' : 'Open notes  (Ctrl+Q)'}
        >
          <Icon name="edit" size={13} />
          <span className="tab-notes-label">Notes</span>
        </ViewButton>
      </div>
      {/* With no rail on screen the figures an advisor glances at move into a
          one-line strip here, so a half-screen window still shows the money. */}
      {(zoomMode || railHidden) && <TabInsights tabId={activeTab} />}
    </nav>
  );
}

const DASH = '—';

// Three figures for the open page, from the same metrics the rail shows.
// Gated the way the rail is (PQDashboard dataState): a fresh record shows
// dashes rather than a row of $0, which read as a verdict with a client
// watching the screen.
export function tabInsightFigures(tabId, data) {
  const m = getDashboardMetrics(data);
  const hasBalance = m.totalAssets > 0 || m.totalLiabilities > 0;
  const hasIncome = m.totalIncome > 0;
  const bal = v => (hasBalance ? formatDollars(v) : DASH);
  const inc = v => (hasIncome ? formatDollars(v) : DASH);
  switch (tabId) {
    case 'balance':
      return [
        { label: 'Investable', value: bal(m.investableAssets) },
        { label: 'Real Estate', value: bal(m.realEstateAssets) },
        { label: 'Net Worth', value: bal(m.totalNetWorth), net: true },
      ];
    case 'cashflow':
      return [
        { label: 'Income', value: inc(m.totalIncome) },
        { label: 'Savings', value: inc(m.totalSavings) },
        { label: 'Net Cash Flow', value: inc(m.cashFlow), net: true, neg: hasIncome && m.cashFlow < 0 },
      ];
    case 'protection': {
      const c = coverageSummary(data);
      return [
        { label: 'Policies', value: c.count ? String(c.count) : DASH },
        { label: 'Death Benefit', value: c.count ? formatDollars(c.deathBenefit) : DASH },
        { label: 'Annual Premiums', value: c.count ? formatDollars(c.premiums) : DASH, net: true },
      ];
    }
    default:
      return [
        { label: 'Total Assets', value: bal(m.totalAssets) },
        { label: 'Total Liabilities', value: bal(m.totalLiabilities) },
        { label: 'Net Worth', value: bal(m.totalNetWorth), net: true },
      ];
  }
}

const num = v => {
  const n = parseFloat(String(v ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};
const LIFE_TYPES = ['Term Life', 'Whole Life', 'Universal Life'];

// What the insurance table adds up to. Death benefit counts life policies
// only: the same Benefit column holds a daily LTC benefit, and adding a $200
// day to a $500,000 policy would say nothing true.
export function coverageSummary(data) {
  const rows = (data.insurance?.policies || []).filter(r =>
    r && (r.company || r.type || r.benefit || r.annualPremium)
  );
  return {
    count: rows.length,
    deathBenefit: rows.filter(r => LIFE_TYPES.includes(r.type)).reduce((a, r) => a + num(r.benefit), 0),
    premiums: rows.reduce((a, r) => a + num(r.annualPremium), 0),
  };
}

function TabInsights({ tabId }) {
  const { data } = useStore();
  const figs = tabInsightFigures(tabId, data);
  return (
    <div className="tab-insights" role="group" aria-label="Key figures">
      {figs.map(f => (
        <span key={f.label} className={'tab-insight' + (f.net ? ' is-net' : '') + (f.neg ? ' is-neg' : '')}>
          <span className="tab-insight-label">{f.label}</span>
          <span className="tab-insight-val">{f.value}</span>
        </span>
      ))}
    </div>
  );
}

// Under the last section of a page: on to the next page, back to the last.
// Newer advisors fill the PQ front to back, and the tab bar is a long way up
// once a page is scrolled.
export function TabFooter({ tabId }) {
  const { setActiveTab } = useLayout();
  const { prev, next } = adjacentTabs(tabId);
  return (
    <div className="tab-footer">
      {prev ? (
        <ViewButton className="btn btn-ghost tab-footer-prev" onClick={() => setActiveTab(prev.id)}>
          <Icon name="chevron-left" size={13} /> {prev.label}
        </ViewButton>
      ) : <span />}
      {/* Not btn-primary: that class is the Save button's, and the suite finds
          Save by it. */}
      {next ? (
        <ViewButton className="btn btn-ghost tab-footer-next" onClick={() => setActiveTab(next.id)}>
          Next: {next.label} <Icon name="chevron-right" size={13} />
        </ViewButton>
      ) : (
        <span className="tab-footer-end">That's the last page of the questionnaire.</span>
      )}
    </div>
  );
}
